import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    TextAreaInput
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    FileIcon
} from "../../anatomy/icons";

const NAME = "TextAreaInput";

const USAGE_CODE = `const [note, setNote] = useState("");

<TextAreaInput
    placeholder="Add a note for the courier"
    spreadBehaviour="stretch"
    isClearEnabled={true}
    onChangeText={setNote}
    title="Order note"
    maxLength={200}
    value={note}
/>`;

const HEIGHT_CODE = `<TextAreaInput
    spreadBehaviour="stretch"
    title="Delivery notes"
    maxHeight={160}
    minHeight={96}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const NOTE_MAX_LENGTH = 120;

const COUNTER_MAX_LENGTH = 80;

const OVER_MAX_LENGTH = 20;

const LIMITED_MAX_HEIGHT = 96;

const TALL_MIN_HEIGHT = 140;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("textAreaInputPage.counterTitle"),
        id: "counter"
    },
    {
        title: t("textAreaInputPage.heightTitle"),
        id: "height"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("textAreaInputPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TextAreaInputPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        note,
        setNote
    ] = useState("");

    return <Article>
        <ComponentHeader
            lead={t("textAreaInputPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("textAreaInputPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("textAreaInputPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("textAreaInputPage.overviewCaption")}
                isStacked={true}
            >
                <TextAreaInput
                    placeholder={t("textAreaInputPage.notePlaceholder")}
                    hintText={t("textAreaInputPage.noteHint")}
                    title={t("textAreaInputPage.noteTitle")}
                    maxLength={NOTE_MAX_LENGTH}
                    spreadBehaviour="stretch"
                    onChangeText={setNote}
                    isClearEnabled={true}
                    value={note}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("textAreaInputPage.counterTitle")}
            id="counter"
        >
            <Paragraph>
                {t("textAreaInputPage.counterText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <TextAreaInput
                    initialValue={t("textAreaInputPage.counterSample")}
                    title={t("textAreaInputPage.counterShown")}
                    maxLength={COUNTER_MAX_LENGTH}
                    spreadBehaviour="stretch"
                />
                <TextAreaInput
                    initialValue={t("textAreaInputPage.counterLongSample")}
                    title={t("textAreaInputPage.counterOver")}
                    maxLength={OVER_MAX_LENGTH}
                    spreadBehaviour="stretch"
                />
                <TextAreaInput
                    title={t("textAreaInputPage.counterHidden")}
                    maxLength={COUNTER_MAX_LENGTH}
                    isShowLengthLimiter={false}
                    spreadBehaviour="stretch"
                />
            </Preview>
        </Section>
        <Section
            title={t("textAreaInputPage.heightTitle")}
            id="height"
        >
            <Paragraph>
                {t("textAreaInputPage.heightText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <TextAreaInput
                    title={t("textAreaInputPage.heightDefault")}
                    placeholder={t("sample.body")}
                    spreadBehaviour="stretch"
                />
                <TextAreaInput
                    title={`minHeight ${TALL_MIN_HEIGHT}`}
                    placeholder={t("sample.body")}
                    minHeight={TALL_MIN_HEIGHT}
                    spreadBehaviour="stretch"
                />
                <TextAreaInput
                    initialValue={t("textAreaInputPage.multiLineSample")}
                    title={`maxHeight ${LIMITED_MAX_HEIGHT}`}
                    maxHeight={LIMITED_MAX_HEIGHT}
                    spreadBehaviour="stretch"
                />
            </Preview>
            <CodeBlock
                code={HEIGHT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("textAreaInputPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <TextAreaInput
                        placeholder={t("sample.body")}
                        spreadBehaviour="stretch"
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("textAreaInputPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("textAreaInputPage.actionsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <TextAreaInput
                    initialValue={t("textAreaInputPage.counterSample")}
                    title={t("textAreaInputPage.descriptionTitle")}
                    spreadBehaviour="stretch"
                    isClearEnabled={true}
                    icon={FileIcon}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("textAreaInputPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <TextAreaInput
                    initialValue={t("textAreaInputPage.counterSample")}
                    title={t("textAreaInputPage.disabled")}
                    spreadBehaviour="stretch"
                    isDisabled={true}
                />
                <TextAreaInput
                    initialValue={t("textAreaInputPage.counterSample")}
                    title={t("textAreaInputPage.readOnly")}
                    spreadBehaviour="stretch"
                    isReadOnly={true}
                />
                <TextAreaInput
                    initialValue={t("textAreaInputPage.counterSample")}
                    title={t("textAreaInputPage.loading")}
                    spreadBehaviour="stretch"
                    isLoading={true}
                />
                <TextAreaInput
                    placeholder={t("sample.body")}
                    hintText={t("sample.error")}
                    spreadBehaviour="stretch"
                    title="danger"
                    type="danger"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("textAreaInputPage.a11yLabel"),
                    t("textAreaInputPage.a11yHint"),
                    t("textAreaInputPage.a11yCounter"),
                    t("textAreaInputPage.a11yStates"),
                    t("textAreaInputPage.a11yActions"),
                    t("textAreaInputPage.a11yKeyboard"),
                    t("textAreaInputPage.a11yDeprecated")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("textAreaInputPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("textAreaInputPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("textAreaInputPage.propOnChangeText"),
                        type: "(value: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("textAreaInputPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("textAreaInputPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("textAreaInputPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("textAreaInputPage.propMaxLength"),
                        name: "maxLength",
                        type: "number"
                    },
                    {
                        description: t("textAreaInputPage.propIsShowLengthLimiter"),
                        name: "isShowLengthLimiter",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propMinHeight"),
                        name: "minHeight",
                        type: "number"
                    },
                    {
                        description: t("textAreaInputPage.propMaxHeight"),
                        defaultValue: "350",
                        name: "maxHeight",
                        type: "number"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("textAreaInputPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("textAreaInputPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("textAreaInputPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("textAreaInputPage.propValidation"),
                        type: "(text: string) => boolean",
                        name: "validation"
                    },
                    {
                        description: t("textAreaInputPage.propIsUpdateOnRealtime"),
                        name: "isUpdateOnRealtime",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("textAreaInputPage.propIsShowSubTitle"),
                        name: "isShowSubTitle",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("textAreaInputPage.propIconOnPress"),
                        name: "iconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("textAreaInputPage.propIconAccessibilityLabel"),
                        name: "iconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("textAreaInputPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        description: t("textAreaInputPage.propRightIconOnPress"),
                        name: "rightIconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("textAreaInputPage.propRightIconAccessibilityLabel"),
                        name: "rightIconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("textAreaInputPage.propHintTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "hintTextIcon"
                    },
                    {
                        description: t("textAreaInputPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("textAreaInputPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("textAreaInputPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("textAreaInputPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("textAreaInputPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("textAreaInputPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TextAreaInputPage;
